// Single source of truth for the homepage's Hungarian and English copy,
// mirroring the pattern already used for /bob (see src/data/bobContent.ts):
// a lang-keyed content object driving every section, with a HU/EN toggle
// in the header. Each language is written as its own native copy, not a
// literal translation of the other.

export type HomeLang = "hu" | "en";

export type HomeContent = {
  meta: { title: string; description: string };
  nav: {
    items: { id: string; label: string; href?: string }[];
    cta: string;
    menuLabel: string;
  };
  hero: {
    headingStart: string;
    headingHighlight: string;
    subtitle: string;
    ctaPrimary: string;
    ctaSecondary: string;
  };
  about: {
    eyebrow: string;
    heading: string;
    intro: string;
    items: { title: string; description: string }[];
  };
  wobble: {
    heading: string;
    body: string;
    bullets: string[];
    fit: string;
    cta: string;
  };
  bob: {
    tagline: string;
    heading: string;
    body: string;
    bullets: string[];
    cta: string;
  };
  rosa: {
    heading: string;
    body: string;
    bullets: string[];
    fit: string;
    cta: string;
    mailSubject: string;
  };
  customDev: {
    eyebrow: string;
    heading: string;
    body: string;
    points: { title: string; description: string }[];
    cta: string;
  };
  faq: {
    eyebrow: string;
    heading: string;
    items: { question: string; answer: string }[];
  };
  contactCTA: {
    heading: string;
    body: string;
    linkedinLabel: string;
  };
  footer: {
    copyright: string;
  };
};

const hu: HomeContent = {
  meta: {
    title: "Miller Productions: Üzleti szoftver kis- és középvállalkozásoknak",
    description:
      "A Miller Productions gyakorlati szoftvert épít kis- és középvállalkozásoknak: a Wobble időpontfoglalástól a BOB üzemeltetési platformon át a Rosa ügyfélszolgálati rendszeréig, valamint egyedi fejlesztést is vállalunk, ha a kész termékek nem elegendők.",
  },
  nav: {
    items: [
      { id: "home", label: "Főoldal" },
      { id: "about", label: "Rólunk" },
      { id: "products", label: "Wobble" },
      { id: "bob", label: "BOB", href: "/bob" },
      { id: "rosa", label: "Rosa" },
      { id: "custom-dev", label: "Egyedi fejlesztés" },
      { id: "faq", label: "Kérdések" },
    ],
    cta: "Kapcsolatfelvétel",
    menuLabel: "Menü megnyitása",
  },
  hero: {
    headingStart: "Gyakorlati szoftverek a vállalkozásod",
    headingHighlight: "mindennapjaihoz",
    subtitle:
      "A Wobble időpontot foglal, a BOB rendben tartja a munkatársakat és a partnereket, a Rosa kezeli az ügyfélmegkereséseket. Ha egyik sem passzol pontosan a céged működéséhez, egyedi szoftvert építünk rá.",
    ctaPrimary: "Lássuk a termékeket",
    ctaSecondary: "Kapcsolatfelvétel",
  },
  about: {
    eyebrow: "KIK VAGYUNK",
    heading: "Gyakorlati szoftver, valós üzleti problémákra",
    intro:
      "A Miller Productions kis- és középvállalkozásoknak épít szoftvert: kész termékeket az időpontfoglalástól az üzemeltetésen át az ügyfélszolgálatig, és egyedi fejlesztést, ha a céged működése ezeknél egyedibb megoldást kíván.",
    items: [
      {
        title: "Fókuszált termékek",
        description:
          "A Wobble, a BOB és a Rosa egy-egy konkrét problémát old meg jól: nem próbálnak mindenes platformként mindenkinek megfelelni.",
      },
      {
        title: "Valódi működésre épül",
        description:
          "Nem egy elméleti funkciólistából indulunk ki, hanem abból, hogyan dolgozik ténylegesen egy kis- vagy középvállalkozás nap mint nap.",
      },
      {
        title: "Egyedi fejlesztés, ha kell",
        description:
          "Ha a céged igényei túlmutatnak a kész termékeken, felmérjük a folyamataidat, és rájuk szabott rendszert tervezünk és építünk.",
      },
    ],
  },
  wobble: {
    heading: ": online időpontfoglalás és foglalási oldal",
    body:
      "Ha az ügyfeleid most is telefonon vagy Messengeren egyeztetik veled az időpontokat, ismerős lehet az ezzel járó oda-vissza üzengetés. A Wobble leveszi rólad az időpontfoglalás terhét. Saját foglalási oldalt kapsz, ahol ügyfeleid egyszerűen lefoglalhatják a nekik megfelelő időpontot, te pedig mindent egy helyen kezelhetsz az adminfelületen.",
    bullets: [
      "Foglalási oldal egyedi megjelenéssel",
      "Weboldal opció blog bejegyzésekkel",
      "Várólista, számlázás, előlegfizetés",
      "Email marketing",
      "Kérdőív a szolgáltatásokhoz",
    ],
    fit: "Kinek jó: pilates oktatóknak, személyi edzőknek, kutyakozmetikusoknak, masszőrőknek, tanácsadóknak, coachoknak és minden vállalkozásnak, ahol az ügyfél időpontra jön.",
    cta: "Megnézem a Wobble-t",
  },
  bob: {
    tagline: "Business Operations Base",
    heading: "BOB: üzemeltetési szoftver KKV-knak",
    body:
      "Ha a csapatodban alkalmazottak és alvállalkozók is dolgoznak, miközben a munkarendek, beosztások és partneradatok különböző táblázatokban és üzenetekben vannak nyilvántartva, a BOB mindezt egyetlen átlátható rendszerben kezeli.",
    bullets: [
      "Munkatársak és alvállalkozók egy nyilvántartásban",
      "Osztályok, munkakörök és munkarendek",
      "Beosztás és jelenlét, automatikusan összekapcsolva",
      "Üzleti partnerek, kapcsolattartók és feladatok",
    ],
    cta: "Ismerd meg a BOB-ot",
  },
  rosa: {
    heading: "Rosa: ügyfélszolgálati és ticketkezelő rendszer",
    body:
      "Következő termékünkön dolgozunk: a Rosa a csapatok közös ügyfélszolgálati postafiókját alakítja át áttekinthető, nyomon követhető rendszerré, hogy egyetlen ügyfélmegkeresés se vesszen el, és mindenki lássa, ki miért felel.",
    bullets: [
      "Közös postafiók a csapatnak",
      "Automatikus kategorizálás felelős szerint",
      "Státuszkövetés minden ügyhöz",
      "Wobble-integráció a foglalásokkal",
    ],
    fit: "Kinek jó: csapatoknak, akik közösen kezelik az ügyfélmegkereséseket, és szeretnék végre egy helyen látni, mi történt egy ügyféllel.",
    cta: "Értesítést kérek az indulásról",
    mailSubject: "Rosa értesítést kérek",
  },
  customDev: {
    eyebrow: "EGYEDI FEJLESZTÉS",
    heading: "Amikor egy kész termék nem elég",
    body:
      "A Wobble, a BOB és a Rosa a leggyakoribb üzleti helyzeteket fedi le. De ha a vállalkozásod működése ennél egyedibb (összetett folyamatok, egyedi integrációk, olyan munkamenet, amit egyik kész szoftver sem követ pontosan), egyedi szoftvert tervezünk és építünk, a valós működésedre szabva.",
    points: [
      {
        title: "Felmérjük a folyamataidat",
        description:
          "Mielőtt kódolni kezdenénk, megértjük, hogyan dolgozik ténylegesen a csapatod: nem egy általános funkciólistából indulunk ki.",
      },
      {
        title: "Termékfejlesztés",
        description:
          "A végleges egyedi szoftver folyamatos igényfelmérés és tesztelés eredményeként készül el.",
      },
      {
        title: "Karbantartható rendszert építünk",
        description:
          "Olyan szoftvert kapsz, amit hosszú távon is lehet bővíteni és üzemeltetni: nem egy egyszeri, ott ragadt megoldást.",
      },
    ],
    cta: "Beszéljünk a projektedről",
  },
  faq: {
    eyebrow: "GYIK",
    heading: "Kérdések, amik felmerülhetnek",
    items: [
      {
        question: "Melyik terméketeket válasszam, ha az ügyfeleim maguk szeretnének időpontot foglalni?",
        answer:
          "A Wobble erre való: önálló foglalási oldalt ad a vállalkozásodnak, ahol az ügyfelek maguk foglalnak időpontot, te pedig egy admin felületen kezeled a szolgáltatásokat és a naptárat. Jó választás masszőröknek, pilates oktatóknak, tanácsadóknak, személyi edzőknek, kutyakozmetikusoknak és minden olyan vállalkozásnak, ahol az ügyfél időpontra jön.",
      },
      {
        question: "Hogyan segít a BOB, ha alkalmazottak és alvállalkozók is dolgoznak a cégemnél?",
        answer:
          "A BOB egy rendszerben tartja a munkatársakat és az alvállalkozókat, a munkaköröket, a munkarendet és a beosztást, valamint az üzleti partnerek adatait. Nem kell táblázatok és üzenetek között keresgélni ahhoz, hogy tudd, ki miért felel, és ki dolgozik ma.",
      },
      {
        question: "Mi lesz a Rosa, és mikor indul?",
        answer:
          "A Rosa a következő termékünk: az ügyfélszolgálati postafiókot alakítja majd át áttekinthető, nyomon követhető ticketkezelő rendszerré, Wobble-integrációval. Jelenleg fejlesztés alatt áll. Ha érdekel, jelezd, és értesítünk az indulásról.",
      },
      {
        question: "Hogyan illeszkednek egymáshoz a termékeik?",
        answer:
          "A Wobble, a BOB és a Rosa önállóan is használhatók, de ugyanazt a gondolkodást követik: egy-egy konkrét üzleti problémára adnak fókuszált választ, nem próbálnak mindenes platformmá válni. Ahol van kapcsolódási pont, például a Wobble-ös foglalások és a Rosa ügyfélmegkeresései között, ott össze is kötjük őket.",
      },
      {
        question: "Mikor van szükségem egyedi szoftverre a kész termékek helyett?",
        answer:
          "Akkor, ha a vállalkozásod működése egyedibb annál, amit egy kész termék le tud fedni (például összetett belső folyamatok, sajátos integrációs igények vagy olyan munkamenet, amit egyik szoftverünk sem követ pontosan). Ilyenkor felmérjük a folyamataidat, és a valós működésedre szabott rendszert tervezünk.",
      },
    ],
  },
  contactCTA: {
    heading: "Beszéljünk a vállalkozásodról?",
    body:
      "Vedd fel velünk a kapcsolatot, és megnézzük, melyik termékünk illik a működésedre, vagy hogy mire van szükség egy egyedi megoldáshoz.",
    linkedinLabel: "LinkedIn",
  },
  footer: {
    copyright: "© 2026 Miller Productions Kft. Minden jog fenntartva",
  },
};

const en: HomeContent = {
  meta: {
    title: "Miller Productions: Business software for small and medium-sized companies",
    description:
      "Miller Productions builds practical software for small and medium-sized businesses: Wobble for appointment booking, BOB for day-to-day operations, and Rosa for customer support, plus custom development when ready-made products aren't enough.",
  },
  nav: {
    items: [
      { id: "home", label: "Home" },
      { id: "about", label: "About" },
      { id: "products", label: "Wobble" },
      { id: "bob", label: "BOB", href: "/bob" },
      { id: "rosa", label: "Rosa" },
      { id: "custom-dev", label: "Custom development" },
      { id: "faq", label: "FAQ" },
    ],
    cta: "Get in touch",
    menuLabel: "Open menu",
  },
  hero: {
    headingStart: "Practical software for your business,",
    headingHighlight: "every day",
    subtitle:
      "Wobble books appointments, BOB keeps your team and partners organized, Rosa handles customer requests. If none of them fit your business exactly, we'll build custom software around it.",
    ctaPrimary: "See our products",
    ctaSecondary: "Get in touch",
  },
  about: {
    eyebrow: "WHO WE ARE",
    heading: "Practical software for real business problems",
    intro:
      "Miller Productions builds software for small and medium-sized businesses: ready-made products for appointment booking, day-to-day operations, and customer support, and custom development when your business runs differently from all three.",
    items: [
      {
        title: "Focused products",
        description:
          "Wobble, BOB, and Rosa each solve one problem well: they're not trying to be an everything platform.",
      },
      {
        title: "Built around how you actually work",
        description:
          "We start from how a small business really operates day to day, not a theoretical feature list.",
      },
      {
        title: "Custom development when it's needed",
        description:
          "When your requirements go beyond what our products cover, we look at your actual process and design a system around it.",
      },
    ],
  },
  wobble: {
    heading: ": online booking and a booking page",
    body:
      "If your customers are still booking appointments over the phone or in Messenger, you know the back-and-forth that comes with it. Wobble takes the booking hassle off your hands. You get your own booking page where customers can book a time that works for them, and everything else is a click away in the admin dashboard.",
    bullets: [
      "Booking page with your own custom look",
      "Optional website with blog posts",
      "Waitlist, invoicing, deposits",
      "Email marketing",
      "Questionnaires for your services",
    ],
    fit: "Good for: pilates instructors, personal trainers, dog groomers, massage therapists, consultants, coaches, and any business where customers come in for an appointment.",
    cta: "See Wobble",
  },
  bob: {
    tagline: "Business Operations Base",
    heading: "BOB: operations software for SMEs",
    body:
      "If your team is a mix of employees and contractors, and schedules, shifts, or partner details are scattered across spreadsheets and messages, BOB brings it into one place.",
    bullets: [
      "Employees and contractors in one record",
      "Departments, roles, and work schedules",
      "Shifts and attendance, automatically linked",
      "Business partners, contacts, and tasks",
    ],
    cta: "Meet BOB",
  },
  rosa: {
    heading: "Rosa: customer support and ticketing",
    body:
      "We're building our next product: Rosa turns a team's shared support inbox into a clear, trackable system, so no customer request gets lost, and everyone can see who's responsible for what.",
    bullets: [
      "Shared inbox for the whole team",
      "Automatic categorization by owner",
      "Status tracking for every request",
      "Wobble integration with bookings",
    ],
    fit: "Good for: teams that handle customer requests together and want one place to see what happened with a customer.",
    cta: "Notify me when it launches",
    mailSubject: "Rosa launch notification",
  },
  customDev: {
    eyebrow: "CUSTOM DEVELOPMENT",
    heading: "When a ready-made product isn't enough",
    body:
      "Wobble, BOB, and Rosa cover the most common business situations. But if your business runs differently (complex internal processes, specific integrations, a workflow none of our products quite follows), we'll design and build custom software around how you actually work.",
    points: [
      {
        title: "We study your process first",
        description:
          "Before we write any code, we learn how your team actually works: not a generic feature checklist.",
      },
      {
        title: "Product development",
        description:
          "The final custom software takes shape through continuous requirements gathering and testing.",
      },
      {
        title: "Software you can maintain, not a one-off",
        description:
          "You get a system that can be extended and supported long-term, not something that gets stuck the day requirements change.",
      },
    ],
    cta: "Let's talk about your project",
  },
  faq: {
    eyebrow: "FAQ",
    heading: "Questions you might have",
    items: [
      {
        question: "Which product should I use if I want customers to book their own appointments?",
        answer:
          "Wobble: it gives you a booking page where customers book themselves, and an admin dashboard to manage services and your calendar. It's a good fit for hair and beauty salons, consultants, tutors, and any business where customers come in for an appointment.",
      },
      {
        question: "How does BOB help if I have both employees and contractors?",
        answer:
          "BOB keeps employees, contractors, roles, schedules, shifts, and business partner data in one system, so you don't have to piece together who's responsible for what and who's working today across spreadsheets and messages.",
      },
      {
        question: "What is Rosa, and when is it launching?",
        answer:
          "Rosa is our next product: it turns a support inbox into a clear, trackable ticketing system, with Wobble integration. It's currently in development. Let us know if you'd like to hear when it launches.",
      },
      {
        question: "How do your products fit together?",
        answer:
          "Wobble, BOB, and Rosa can each be used on their own, but they follow the same thinking: a focused answer to one real business problem, not an attempt to be an everything platform. Where there's a real connection point, like Wobble bookings and Rosa customer requests, we build it in.",
      },
      {
        question: "When do I need custom software instead of one of your products?",
        answer:
          "When your business runs in a way none of our products quite covers (complex internal processes, specific integration needs, or a workflow that doesn't match any of our products closely enough), we'll look at how you actually work and design a system around it.",
      },
    ],
  },
  contactCTA: {
    heading: "Let's talk about your business",
    body:
      "Get in touch, and we'll figure out which product fits how you work, or what a custom solution would need to cover.",
    linkedinLabel: "LinkedIn",
  },
  footer: {
    copyright: "© 2026 Miller Productions Kft. All rights reserved",
  },
};

export const homeContent: Record<HomeLang, HomeContent> = { hu, en };
